import { createFileRoute } from '@tanstack/react-router'
import { useMemo, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import { ScrollableDataRegion } from '~/components/data/ScrollableDataRegion'
import {
  subscriptionsKeys,
  type FraudFlag,
} from '~/features/subscriptions'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/subscriptions/fraud` — Fraud Detection (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `severity`, `status`.
 *
 * Placeholder until heuristics + Stripe Radar polling are wired to a
 * real endpoint. The fetcher degrades to an empty list when the route
 * isn't deployed yet — matches the prior `fraudQuery` behavior.
 */
const fraudSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  severity: z.enum(['all', 'critical', 'high', 'medium', 'low']).default('all'),
  status: z
    .enum(['all', 'open', 'investigating', 'resolved', 'false_positive'])
    .default('all'),
})
type FraudSearch = z.infer<typeof fraudSearchSchema>

const URL_DEFAULTS: FraudSearch = {
  page: 1,
  limit: 25,
  severity: 'all',
  status: 'all',
}

export const Route = createFileRoute('/admin/subscriptions/fraud')({
  validateSearch: fraudSearchSchema,
  component: FraudPage,
})

interface FraudListPayload {
  readonly flags?: readonly FraudFlag[]
  readonly items?: readonly FraudFlag[]
  readonly total?: number
  readonly totals?: {
    readonly open: number
    readonly resolved_30d: number
  }
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

interface FraudResultPage extends PaginatedResult<FraudFlag> {
  readonly totals?: { readonly open: number; readonly resolved_30d: number }
}

async function fetchFraudPage(
  params: PaginatedParams,
): Promise<FraudResultPage> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sort) query.sort = params.sort
  if (params.filters?.severity && params.filters.severity !== 'all') {
    query.severity = params.filters.severity
  }
  if (params.filters?.status && params.filters.status !== 'all') {
    query.status = params.filters.status
  }

  try {
    const raw = await http.get<FraudListPayload>(
      '/api/v1/admin/subscriptions/fraud',
      { query },
    )
    const rows = (raw.flags ?? raw.items ?? []) as readonly FraudFlag[]
    const total = raw.pagination?.total ?? raw.total ?? rows.length
    const normalized = toPaginatedResult(rows, total, params, raw.pagination)
    return { ...normalized, ...(raw.totals ? { totals: raw.totals } : {}) }
  } catch {
    return toPaginatedResult<FraudFlag>([], 0, params)
  }
}

function FraudPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(fraudSearchSchema, URL_DEFAULTS)

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    filters: {
      severity: search.severity,
      status: search.status,
    },
  }

  const fraud = usePaginatedQuery<FraudFlag>({
    queryKey: [...subscriptionsKeys.fraud(), 'paginated'],
    params,
    fetcher: fetchFraudPage,
  })

  const flags = fraud.data?.data ?? []
  const totals = useMemo(
    () => (fraud.data as FraudResultPage | undefined)?.totals,
    [fraud.data],
  )
  const pagination = fraud.data?.pagination

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Fraud Detection</h1>
          <p className="ka-page-header-subtitle">
            {totals
              ? `${totals.open} open · ${totals.resolved_30d} resolved 30d`
              : 'Heuristic flags + Stripe Radar webhooks'}
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button type="button" className="ka-btn ka-btn--primary">
            Re-run Detection
          </button>
        </div>
      </header>

      <div className="ka-filter-strip" role="search">
        <div className="ka-filter-group" role="group" aria-label="Severity">
          <span className="ka-filter-group-label">Severity</span>
          {(['all', 'critical', 'high', 'medium', 'low'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`ka-detail-chip${search.severity === s ? ' is-active' : ''}`}
              aria-pressed={search.severity === s}
              onClick={() => setUrl({ severity: s, page: 1 })}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div className="ka-filter-group" role="group" aria-label="Status">
          <span className="ka-filter-group-label">Status</span>
          {(
            ['all', 'open', 'investigating', 'resolved', 'false_positive'] as const
          ).map((s) => (
            <button
              key={s}
              type="button"
              className={`ka-detail-chip${search.status === s ? ' is-active' : ''}`}
              aria-pressed={search.status === s}
              onClick={() => setUrl({ status: s, page: 1 })}
            >
              {s
                .split('_')
                .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                .join(' ')}
            </button>
          ))}
        </div>
      </div>

      {flags.length === 0 && !fraud.isPending ? (
        <div className="ka-card ka-subs-empty-card">
          <div className="ka-empty">
            <div className="ka-empty-title">No flags match</div>
            <div className="ka-empty-body">
              Heuristic detection + Stripe Radar polling surface suspicious
              activity here. Adjust filters or run detection manually.
            </div>
          </div>
        </div>
      ) : (
        <div className="ka-card ka-card--flush ka-subs-table-card">
          <ScrollableDataRegion label="Fraud detection results" minWidth="840px">
            <table className="ka-table">
            <thead>
              <tr>
                <th scope="col">Severity</th>
                <th scope="col">Flag</th>
                <th scope="col">Subject</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
                <th scope="col">Detected</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.id}>
                  <td>
                    <SeverityChip severity={f.severity} />
                  </td>
                  <td>{f.flag}</td>
                  <td>{f.subject_label ?? '—'}</td>
                  <td>
                    <span className="ka-chip ka-chip--muted">{f.source}</span>
                  </td>
                  <td>
                    <span className="ka-chip ka-chip--info">{f.status}</span>
                  </td>
                  <td className="ka-td-meta">
                    {f.detected_at.slice(0, 19).replace('T', ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </ScrollableDataRegion>
        </div>
      )}

      <DataTablePagination
        className="ka-subs-list-pagination"
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? flags.length}
        isLoading={fraud.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />
    </div>
  )
}

function SeverityChip({
  severity,
}: {
  readonly severity: 'critical' | 'high' | 'medium' | 'low'
}): ReactElement {
  if (severity === 'critical')
    return <span className="ka-chip ka-chip--destructive">Critical</span>
  if (severity === 'high')
    return <span className="ka-chip ka-chip--destructive">High</span>
  if (severity === 'medium')
    return <span className="ka-chip ka-chip--warning">Medium</span>
  return <span className="ka-chip ka-chip--muted">Low</span>
}
